/**
 * Analytics Service
 *
 * Centralized analytics tracking for user behavior.
 * Can be integrated with PostHog, Mixpanel, or other analytics providers.
 */

import posthog from 'posthog-js'

// Google Ads gtag types
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetOrEvent: string,
      params?: Record<string, string | number | boolean>
    ) => void
    dataLayer?: unknown[]
  }
}

interface UserProperties {
  subscription_tier?: string
  signup_date?: string
  email?: string
  user_id?: string
}

interface EventProperties {
  [key: string]: string | number | boolean | undefined
}

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
// Use reverse proxy in production to bypass ad blockers
const POSTHOG_HOST = import.meta.env.PROD 
  ? '/ingest' 
  : (import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com')
const ENABLE_ANALYTICS =
  Boolean(POSTHOG_KEY) &&
  (import.meta.env.VITE_ENABLE_ANALYTICS === 'true' || import.meta.env.PROD)

class Analytics {
  private enabled: boolean
  private initialized: boolean = false
  private userId: string | null = null

  constructor() {
    this.enabled = ENABLE_ANALYTICS
    // Note: PostHog init is now deferred until consent is granted
    // Call analytics.initialize() after user consents to analytics cookies
  }

  /**
   * Initialize PostHog tracking (only call after user consents)
   */
  initialize() {
    if (!this.enabled || this.initialized) {
      return
    }

    posthog.init(POSTHOG_KEY as string, {
      api_host: POSTHOG_HOST,
      autocapture: false, // keep explicit events only
      capture_pageview: false, // handled manually for SPA routing
      persistence: 'localStorage',
      disable_session_recording: true, // privacy: no session replay by default
      // ui_host must point to PostHog's actual domain for features like toolbar
      ui_host: import.meta.env.PROD ? 'https://eu.posthog.com' : undefined,
    })

    this.initialized = true

    if (import.meta.env.DEV) {
      console.log('[Analytics] PostHog initialized')
    }
  }

  /**
   * Check if analytics is initialized and tracking
   */
  isInitialized(): boolean {
    return this.initialized
  }

  /**
   * Called when user grants analytics consent
   */
  onConsentGranted() {
    if (!this.enabled) {
      return
    }

    if (!this.initialized) {
      this.initialize()
    } else {
      // Re-enable tracking if previously opted out
      posthog.opt_in_capturing()
    }

    if (import.meta.env.DEV) {
      console.log('[Analytics] Consent granted, tracking enabled')
    }
  }

  /**
   * Called when user revokes analytics consent
   */
  onConsentRevoked() {
    if (!this.enabled || !this.initialized) {
      return
    }

    posthog.opt_out_capturing()
    posthog.reset()

    if (import.meta.env.DEV) {
      console.log('[Analytics] Consent revoked, tracking disabled')
    }
  }

  /**
   * Initialize analytics with user context
   */
  identify(userId: string, properties?: UserProperties) {
    if (!this.enabled || !this.initialized) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Identify (not initialized):', userId, properties)
      }
      return
    }

    this.userId = userId

    posthog.identify(userId, properties)

    // Ensure super properties are attached to future events
    if (properties) {
      posthog.register(properties)
    }
  }

  /**
   * Track an event
   */
  track(eventName: string, properties?: EventProperties) {
    if (!this.enabled || !this.initialized) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Track (not initialized):', eventName, properties)
      }
      return
    }

    // Enrich properties with metadata
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      user_id: this.userId,
    }

    posthog.capture(eventName, enrichedProperties)

    // Development logging
    if (import.meta.env.DEV) {
      console.log('[Analytics] Track:', eventName, enrichedProperties)
    }
  }

  /**
   * Track page view
   */
  page(pageName: string, properties?: EventProperties) {
    this.track('page_view', {
      page: pageName,
      ...properties,
    })
  }

  /**
   * Update user properties
   */
  setUserProperties(properties: UserProperties) {
    if (!this.enabled || !this.initialized) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Set user properties (not initialized):', properties)
      }
      return
    }

    posthog.register(properties)
  }

  /**
   * Reset analytics (on logout)
   */
  reset() {
    this.userId = null

    if (!this.enabled || !this.initialized) {
      if (import.meta.env.DEV) {
        console.log('[Analytics] Reset (not initialized)')
      }
      return
    }

    posthog.reset()
  }

  /**
   * Track Google Ads conversion for user registration
   */
  trackRegistrationConversion(userId: string) {
    if (!this.enabled || typeof window === 'undefined' || !window.gtag) {
      console.log('[Analytics] Registration conversion:', userId)
      return
    }

    window.gtag('event', 'conversion', {
      send_to: 'AW-17717746667/se4bCOOPoL0bEOu3vYBC',
      value: 0,
      currency: 'USD',
      transaction_id: userId,
    })
  }

  /**
   * Track Google Ads conversion for subscription purchase
   */
  trackSubscriptionConversion(subscriptionId: string, amount: number, currency: string = 'USD') {
    if (!this.enabled || typeof window === 'undefined' || !window.gtag) {
      console.log('[Analytics] Subscription conversion:', subscriptionId, amount)
      return
    }

    window.gtag('event', 'conversion', {
      send_to: 'AW-17717746667/DW-2CPOroL0bEOu3vYBC',
      value: amount,
      currency: currency,
      transaction_id: subscriptionId,
    })
  }
}

// Singleton instance
export const analytics = new Analytics()

// Event tracking helpers
export const trackEvent = {
  // Auth events
  signup: (method: 'email' | 'google' | 'github', userId?: string) => {
    analytics.track('signup', { method })

    // Track Google Ads conversion for new registrations
    if (userId) {
      analytics.trackRegistrationConversion(userId)
    }
  },

  login: (method: 'email' | 'google' | 'github') => {
    analytics.track('login', { method })
  },

  logout: () => {
    analytics.track('logout')
  },

  // Transformation events
  transformationCompleted: (tone: string, characterCount: number, processingTime: number) => {
    analytics.track('transformation_completed', {
      tone,
      character_count: characterCount,
      processing_time_ms: processingTime,
    })
  },

  transformationFailed: (tone: string, error: string) => {
    analytics.track('transformation_failed', {
      tone,
      error,
    })
  },

  // Subscription events
  upgrade: (
    tier: string,
    billingInterval: 'monthly' | 'yearly',
    subscriptionId?: string,
    amount?: number
  ) => {
    analytics.track('upgrade', {
      tier,
      billing_interval: billingInterval,
    })

    // Track Google Ads conversion for subscription purchases
    if (subscriptionId && amount !== undefined) {
      analytics.trackSubscriptionConversion(subscriptionId, amount)
    }
  },

  downgrade: (fromTier: string, toTier: string) => {
    analytics.track('downgrade', {
      from_tier: fromTier,
      to_tier: toTier,
    })
  },

  subscriptionCancelled: (tier: string) => {
    analytics.track('subscription_cancelled', {
      tier,
    })
  },

  // Feature usage events
  favoriteAdded: (transformationId: string) => {
    analytics.track('favorite_added', {
      transformation_id: transformationId,
    })
  },

  favoriteRemoved: (transformationId: string) => {
    analytics.track('favorite_removed', {
      transformation_id: transformationId,
    })
  },

  historyExported: (count: number) => {
    analytics.track('history_exported', {
      transformation_count: count,
    })
  },

  historySearched: (query: string) => {
    analytics.track('history_searched', {
      query,
    })
  },

  // Engagement events
  helpArticleViewed: (articleId: string) => {
    analytics.track('help_article_viewed', {
      article_id: articleId,
    })
  },

  pricingPageViewed: () => {
    analytics.track('pricing_page_viewed')
  },

  quotaLimitReached: (tier: string) => {
    analytics.track('quota_limit_reached', {
      tier,
    })
  },

  // Chrome extension events
  chromeExtensionClicked: (location: 'hero' | 'section' | 'footer' | 'feature') => {
    analytics.track('chrome_extension_clicked', {
      location,
    })
  },
}
